import React from 'react';

export type OverlayListItem = {
  id: string;
  kind: 'heading' | 'button';
  title: string;
};

type Props = {
  top: number;
  left: number;
  items: OverlayListItem[];
  onClose: () => void;
  onSettings?: (id: string, kind: 'heading'|'button', anchor: HTMLElement) => void;
  onDelete?: (id: string, kind: 'heading'|'button') => void;
  onDuplicate?: (id: string, kind: 'heading'|'button') => void;
  onMoveUp?: (id: string, kind: 'heading'|'button') => void;
  onMoveDown?: (id: string, kind: 'heading'|'button') => void;
};

const CellOverlayListPopup2: React.FC<Props> = ({ top, left, items, onClose, onSettings, onDelete, onDuplicate, onMoveUp, onMoveDown }) => {
  return (
    <div
      className="fixed z-[2100] w-80 rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-xl"
      style={{ top, left }}
      onClick={(e)=> { e.stopPropagation(); }}
    >
      <div className="flex items-center justify-between px-3 pt-3">
        <div className="text-sm font-medium text-gray-900">Cell elements</div>
        <button
          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          title="Close"
          aria-label="Close list"
        >
          <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="px-3 pb-3">
        {(!items || items.length === 0) && (
          <div className="text-xs text-gray-500 py-2">No elements yet.</div>
        )}
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition">
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-gray-50 border border-gray-200 text-gray-700">
                {it.kind === 'heading' ? (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 4v16"/><path d="M18 4v16"/><path d="M6 12h12"/></svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="8" width="16" height="8" rx="4"/></svg>
                )}
              </span>
              <span className="text-sm font-medium text-gray-800 truncate flex-1">{it.title}</span>
              <button
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
                title="Move up"
                onClick={(e)=> { e.preventDefault(); e.stopPropagation(); onMoveUp?.(it.id, it.kind); }}
              >
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5l-7 7h14l-7-7z"/></svg>
              </button>
              <button
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
                title="Move down"
                onClick={(e)=> { e.preventDefault(); e.stopPropagation(); onMoveDown?.(it.id, it.kind); }}
              >
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7H5l7 7z"/></svg>
              </button>
              <button
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
                title="Duplicate"
                onClick={(e)=> { e.preventDefault(); e.stopPropagation(); onDuplicate?.(it.id, it.kind); }}
              >
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><rect x="2" y="2" width="13" height="13" rx="2"/></svg>
              </button>
              <button
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
                title="Delete"
                onClick={(e)=> { e.preventDefault(); e.stopPropagation(); onDelete?.(it.id, it.kind); }}
              >
                <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
              </button>
              <button
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50"
                title="Settings"
                onClick={(e)=> { e.preventDefault(); e.stopPropagation(); onSettings?.(it.id, it.kind, e.currentTarget as HTMLElement); }}
              >
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06c.46.46.6 1.14.33 1.73.14.32.33.62.57.87.24.24.54.43.86.57.59.27 1.27.13 1.73-.33l.06-.06"/></svg>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CellOverlayListPopup2;
